import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Steps } from 'antd';

import { successToast, warningToast } from '../../store/slices/toaster.slice';

import { PAYMENT_METHODS } from '../../constants/payments.constant';
import { EMPLOYER_CHANGE_TYPE, STATUS } from '../../constants/status.constant';
import { RequestContext } from '../../context/Request.context';
import { ClientAdvisorBadge } from '../ClientAdvisorBadge/ClientAdvisorBadge';
import { RequestDeletionButton } from '../RequestDeletionButton/RequestDeletionButton';
import { RequestFooter } from '../RequestFooter/RequestFooter';
import { RequestSummaryFooter } from '../RequestSummaryFooter/RequestSummaryFooter';
import { BaseButtonLink, BaseButtonOutline, BaseLoaderSpinner, BaseStatus, PaymentButton } from '..';
import AppConfig from '../../providers/AppConfig';
import { isFormEditable } from '../../helpers/form.helper';
import { isPaymentProceededSuccessfully } from '../../common/common.helpers';
import { scrollToTop } from '../../helpers/scrollTop.helpers';
import { FORM_VARIANT } from '../../constants/form.constant';
import { STEP_VARIANT } from '../../constants/steps.constant';

import './RequestStepWrapper.scss';
import { useCallback } from 'react';
import { DEFAULT_COMPANY } from '../../../registry/src/components/RequestForm/constants';

const { REQUEST_DETAILS, REQUEST_FORM, MAIN } = FORM_VARIANT;

export const RequestStepWrapper = (props) => {
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();

  const isAdmin = useSelector((state) => state.user.details.is_admin);
  const createdAt = useSelector((state) => state.user.details.created_at);

  const {
    children,
    isFinal,
    validationSubmit,
    content,
    disabled,
    noSteps,
    finalRedirect,
    isAdvisor,
    adminRedirect,
    togglePublish,
    goBack,
    formVariant = MAIN,
    setActiveCategory,
    activeCategory,
    categories,
    steps,
    activeStep,
    setActiveStep,
    isIntro,
    isRenewal,
    setEditForm,
    editForm,
    isCodeOfConduct,
    setIsSummaryCompleted,
    isEmployerChange,
    isUpdateRequire,
  } = props;

  const {
    cancelAmendment,
    hydrateList,
    metadata,
    saveStep,
    submitForm,
    clearForm,
    clearAfterSubmit,
    request,
    validateFormFnRef,
    setCompletedState,
    restoreCompanies,
    formRef,
  } = useContext(RequestContext);

  const paymentRef = useRef(null);

  const onPaymentClose = () => {
    if (finalRedirect) {
      navigate(finalRedirect);
    }
  };

  const confirmationsRef = useRef(null);

  const originalCompaniesRef = useRef(request.companies ? [...request.companies] : []);

  // Save reference to Confirmations component methods
  const setConfirmationsRef = useCallback((ref) => {
    confirmationsRef.current = ref;
  }, []);

  const isClientAdvisorBadgeVisible = isAdvisor && request.status === STATUS.APPROVED && request.badge_png_file;
  const { SUMMARY, PAYMENTS_CONFIRMATIONS, FIELDS_OF_ACTIVITY, PERSONAL_INFORMATION } = STEP_VARIANT;
  const isSummary = steps && steps[activeStep - 1][0] === SUMMARY;
  const isFieldsOfActivity = steps && steps[activeStep - 1][0] === FIELDS_OF_ACTIVITY;
  const isPaymentsConfirmations = steps && steps[activeStep - 1][0] === PAYMENTS_CONFIRMATIONS;
  const isAmendment = request.amendment && AppConfig.editStatuses.includes(request.amendment.status);
  const isPaymentSuccess = isAdvisor && !isPaymentProceededSuccessfully(request.payment);
  const isPaymentSuccessEmployerChange =
    request.amendment && isAdvisor && !isPaymentProceededSuccessfully(request.amendment.payment);
  const isPaymentSuccessRenewal =
    request.amendment && isAdvisor && !isPaymentProceededSuccessfully(request.amendment.payment);

  const isRequestDetails = formVariant === REQUEST_DETAILS;
  const isRequestForm = formVariant === REQUEST_FORM;
  const isMainForm = formVariant === MAIN;

  const isRenewalAllowed = request.renewal_allowed;

  useEffect(() => {
    // We need to pass reference of validation to context function to be able to use it when saving
    // from outside of the form scope
    validateFormFnRef.current = validationSubmit;
  }, [validateFormFnRef, validationSubmit]);

  if (!request) {
    return <BaseLoaderSpinner large />; // Or return null if you prefer a blank screen while loading
  }

  const displayErrorToast = (toastType = 'somethingWentWrong') => {
    warningToast(common(`request.toasts.${toastType}Message`), common(`request.toasts.${toastType}Title`));
  };

  const displayWarningToast = (toastType = 'validationWarning') => {
    warningToast(common(`request.toasts.${toastType}Message`), common(`request.toasts.${toastType}Title`));
  };

  const displaySuccessToast = (toastType = 'saveAndReturn') => {
    successToast(common(`request.toasts.${toastType}Message`), common(`request.toasts.${toastType}Title`));
  };

  const onSuccess = (toastType = 'requestSubmitted') => {
    displaySuccessToast(toastType);
    clearAfterSubmit();
    sessionStorage.removeItem('regsevices-form-values-additional-email');
    if (finalRedirect) {
      navigate(finalRedirect);
    }
  };

  const saveAndReturn = () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(() => saveStep(form.values))
        .then(() => {
          displaySuccessToast();
          if (!noSteps) {
            clearForm();
            navigate('/');
          }
        })
        .catch(() => {});
    }, false);
  };

  const saveAndContinue = () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm) // TODO: This is hack to revalidate | check formik 2.0 when it's out
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
            return;
          }

          saveStep(form.values)
            .then(() => {
              if (noSteps) {
                setCompletedState(true);
                navigate(adminRedirect && isAdmin ? `${adminRedirect}/${request.case_id}` : '/');
                return;
              }

              if (metadata.requestCompleted) {
                props.lastStep();
              } else {
                props.nextStep();
              }
            })
            .catch((error) => {
              console.error('RequestStepWrapper::saveAndContinue:: saveStep failed with error:', error);
            });
        });
    });
  };

  const saveAndSubmit = () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
          }
          return !hasErrors;
        })
        .then(async (validationResults) => {
          if (validationResults) {
            if (confirmationsRef.current) {
              const confirmationValues = confirmationsRef.current.getValues();
              await confirmationsRef.current.updateConfirmationValues(confirmationValues);
            }

            const result = await submitForm();

            if (result) {
              if (result.status) {
                return onSuccess('unnecessarySubmissionAllGood');
              }

              if (
                isAdvisor &&
                !isPaymentProceededSuccessfully(
                  isRenewal || isEmployerChange ? request.amendment.payment : request.payment
                ) &&
                form.values.payment.method === PAYMENT_METHODS.CREDIT_CARD
              ) {
                try {
                  paymentRef.current.clickButton();
                } catch (e) {
                  displayErrorToast();
                }
              } else {
                onSuccess();
              }
            }
          }
        });
    });
  };

  const nextStepSubmit = () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
          } else {
            form.setTouched({});
            setActiveStep(activeStep + 1);
            scrollToTop();
          }
          return !hasErrors;
        })
        .then(async (validationResults) => {
          if (validationResults) {
            try {
              await saveStep(form.values);
              await hydrateList();
            } catch (e) {
              console.error('RequestStepWrapper::nextStepSubmit:: error during saveStep or hydrateList', e);
              displayWarningToast();
            }
          }
        });
    });
  };

  const submitAmendment = async () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
          }
          return !hasErrors;
        })
        .then(async (validationResults) => {
          if (validationResults) {
            try {
              await saveStep(form.values);

              if (isRenewal || isEmployerChange) {
                await hydrateList();
                await setActiveStep(activeStep + 1);
                sessionStorage.setItem('regsevices-form-values', JSON.stringify({ ...form.values, case_id: request.case_id }));
              }

              if (!isRenewal && !isEmployerChange) {
                await submitForm(true, false, false, form.values.additional_recipients);
                await hydrateList();
                displaySuccessToast('amendmentSubmitted');
                navigate('/request-details');
              }
            } catch (e) {
              displayWarningToast();
            }
          }
        });
    });
  };

  const submitRequireUpdate = async () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
          }
          return !hasErrors;
        })
        .then(async (validationResults) => {
          if (validationResults) {
            try {
              await saveStep(form.values);
              // We use amendment for require update because payment have already done on initial
              await submitForm(true, false, false, request.additional_recipients);
              await hydrateList();
              displaySuccessToast('unnecessarySubmissionAllGood');
              navigate('/request-details');
            } catch (e) {
              displayWarningToast();
            }
          }
        });
    });
  };

  const saveAndContinueSteps = () => {
    if (isSummary) {
      !isUpdateRequire ? [setActiveStep(activeStep + 1), scrollToTop()] : submitRequireUpdate();
    }
    !isSummary && !isPaymentsConfirmations && nextStepSubmit();
    isPaymentsConfirmations && saveAndSubmit();
  };

  const uploadSessionStorage = (firstname, lastname) => {
    sessionStorage.setItem('regsevices-form-values-firstname', JSON.stringify(firstname));
    sessionStorage.setItem('regsevices-form-values-lastname', JSON.stringify(lastname));
  };

  const nextStepAmendment = (isRenewal = false, isEmployerChange = false) => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            displayWarningToast();
          }
          return !hasErrors;
        })
        .then(async (validationResults) => {
          if (validationResults) {
            let valuesToSave = { ...form.values };

            let isNewInEmployerChange = false;
            const typeChanged = form.initialValues.employer_change_type !== valuesToSave.employer_change_type;
            const hasInProgressCompany =
              !typeChanged && (valuesToSave.companies || []).some((c) => c.is_new_in_employer_change);
            if (isEmployerChange && activeStep === 1 && !hasInProgressCompany) {
              if (valuesToSave.employer_change_type === EMPLOYER_CHANGE_TYPE.CHANGE_OF_EMPLOYER) {
                valuesToSave.companies = [DEFAULT_COMPANY];
                isNewInEmployerChange = true;
              } else {
                valuesToSave.companies =
                  request.companies && request.companies.length > 0 ? request.companies : [DEFAULT_COMPANY];
              }
              await form.setFieldValue('companies', valuesToSave.companies, false);
            }

            let companiesWithIsNewMarker = null;
            try {
              const result = await saveStep(valuesToSave);
              if (isNewInEmployerChange) {
                companiesWithIsNewMarker = result.companies.map((company) => {
                  return {
                    ...company,
                    is_new_in_employer_change: true,
                  };
                });

                form.setFieldValue('companies', companiesWithIsNewMarker);
              }
              // await hydrateList();
            } catch (e) {
              displayWarningToast();
              return;
            }

            form.setTouched({});
            setActiveStep(activeStep + 1);
            if (isRenewal) {
              setEditForm(false);
              uploadSessionStorage(form.values.first_name, form.values.last_name);
            }
            if (isRenewal || isEmployerChange) {
              const stored = { ...form.values, case_id: request.case_id };
              if (companiesWithIsNewMarker) {
                stored.companies = companiesWithIsNewMarker;
              }
              sessionStorage.setItem('regsevices-form-values', JSON.stringify(stored));
            }
            scrollToTop();
          }
        })
        .catch((err) => {
          console.error('Error during nextStepAmendment:', err);
        });
    });
  };

  const amendmentSaveAndSubmit = () => {
    isSummary ? submitAmendment() : nextStepAmendment();
  };

  const handleBack = async () => {
    if (isEmployerChange && activeStep === 2) {
      const form = formRef.current;

      const companiesToRestore = [];
      const companiesToRemove = [];
      if (originalCompaniesRef.current.length > 0) {
        originalCompaniesRef.current.forEach((originalCompany) => {
          companiesToRestore.push(originalCompany.id);
        });
      }

      form.values.companies.forEach((company) => {
        if (company.id && !companiesToRestore.includes(company.id)) {
          companiesToRemove.push(company.id);
        }
      });

      try {
        const result = await restoreCompanies({
          restore_company_ids: companiesToRestore,
          remove_company_ids: companiesToRemove,
        });
        await form.setFieldValue('companies', result.companies, false);
      } catch (e) {
        console.error('Error restoring companies on back navigation:', e);
      }
    }

    setActiveStep(activeStep - 1);
    scrollToTop();
  };

  const handleBackRenewal = async () => {
    setActiveStep(activeStep - 1);
    setEditForm(false);
    scrollToTop();
  };

  const handleNext = async () => {
    const form = formRef.current;

    validationSubmit(() => {
      form
        .submitForm()
        .then(form.validateForm)
        .then((errors) => {
          const hasErrors = Boolean(Object.keys(errors).length);
          if (hasErrors) {
            setEditForm(true);
            scrollToTop();
            displayWarningToast();
            return;
          }
          setActiveStep(activeStep + 1);
          uploadSessionStorage(form.values.first_name, form.values.last_name);
          scrollToTop();
        })
        .catch(() => {});
    });
  };

  const goToEditForm = async () => {
    setEditForm(true);
    scrollToTop();
  };

  const cancelEditForm = async () => {
    const form = formRef.current;

    const cancelChangeData = Object.keys(form.fields).reduce((acc, item, index) => {
      if (item.includes('education')) {
        const name = item.split('education.')[1];
        if (index === 0) {
          acc['education'] = {
            ...acc['education'],
            ['has_finsa_certificates']: form.initialValues.education['has_finsa_certificates'],
            ['finsa_certificates']: form.initialValues.education['finsa_certificates'],
          };
        }
        if (!item.includes('degrees_temp')) {
          acc['education'] = {
            ...acc['education'],
            [name]: form.initialValues.education[name],
          };
        } else {
          acc['education'] = {
            ...acc['education'],
            degrees_temp: {
              type: form.initialValues.education.degrees_temp.type,
              details: form.initialValues.education.degrees_temp.details,
            },
          };
        }
      } else if (item.includes('companies')) {
        acc['companies'] = form.initialValues['companies'];
      } else {
        acc[item] = form.initialValues[item];
      }
      return acc;
    }, {});

    const currentValues =
      steps[activeStep - 1][0] === STEP_VARIANT.PROFFESSIONAL_ACADEMIC_DEGREES
        ? Object.assign({}, form.values, cancelChangeData)
        : { ...form.values, ...cancelChangeData };
    Object.entries(currentValues).forEach(([key, value]) => form.setFieldValue(key, value));
    setEditForm(false);
    scrollToTop();
  };

  const renewalSaveAndSubmit = () => {
    isSummary && [submitAmendment(), scrollToTop(), setIsSummaryCompleted(true)];
    !isSummary && !isPaymentsConfirmations && nextStepAmendment(isRenewal, false);
    isPaymentsConfirmations && saveAndSubmit();
  };

  const employerChangeSaveAndSubmit = () => {
    isSummary && [submitAmendment(), scrollToTop(), setIsSummaryCompleted(true)];
    !isSummary && !isPaymentsConfirmations && nextStepAmendment(false, isEmployerChange);
    isPaymentsConfirmations && saveAndSubmit();
  };

  const goToEdit = async () => {
    await saveStep({
      amendment: { new: true },
    });
    navigate(`/request-form/${isAdmin ? request.case_id : ''}`);
  };

  const goToEditRenewal = async () => {
    sessionStorage.removeItem('regsevices-form-values');
    await saveStep({
      // When you want test Renewal process on local environment amendment: change to { new: true }
      amendment: { new: true, type: 2 },
    });
    navigate(`/request-form/${isAdmin ? request.case_id : ''}`);
  };

  const goToEditEmployer = async () => {
    sessionStorage.removeItem('regsevices-form-values');
    await saveStep({
      amendment: { new: true, type: 3 },
    });
    navigate(`/request-form/${isAdmin ? request.case_id : ''}`);
  };

  const onCancelAmendmentClick = async () => {
    await cancelAmendment(request.case_id);
    await hydrateList();
  };

  const onCancelRenewalPayment = async () => {
    await cancelAmendment(request.case_id);
    await hydrateList();
    navigate(`/request-details/${request.case_id}`);
    sessionStorage.removeItem('regsevices-form-values');
  };

  const handleDeletionRequestDone = () => {
    hydrateList('/client-advisors/');
  };

  const showDetails = (formVariant) => (
    <BaseStatus
      showDetails
      isAdvisor={isAdvisor}
      isPublishedOnRegister={request.is_published}
      handleSwitchChange={togglePublish}
      {...request}
      data-testid="request-status"
      formVariant={formVariant}
    />
  );

  const showStatus = () => {
    const iconOptions = {
      iconLeft: isRequestDetails,
      full: isRequestDetails,
      left: isRequestDetails,
      iconRight: !isRequestDetails,
      small: true,
      className: 'multiline-button',
    };

    return (
      isAdvisor &&
      AppConfig.approvedStatuses.includes(request.status) && (
        <div>
          {!props.isDeletionProcessStarted && (
            <>
              {(!request.amendment ||
                (request.amendment && AppConfig.resolvedStatuses.includes(request.amendment.status))) && (
                <>
                  <div className="base-status__info">
                    <BaseButtonOutline
                      onClick={goToEdit}
                      {...iconOptions}
                      icon="pencil"
                      data-testid="details-request-change-button"
                    >
                      {common('request.details.requestChange')}
                    </BaseButtonOutline>
                  </div>
                  <div className="base-status__info">
                    <BaseButtonOutline
                      onClick={goToEditEmployer}
                      {...iconOptions}
                      icon="pencil"
                      data-testid="details-request-change-button"
                    >
                      {common('request.details.requestEmployerChange')}
                    </BaseButtonOutline>
                  </div>
                  {isRequestDetails && isRenewalAllowed && (
                    <div className="base-status__info">
                      <BaseButtonOutline
                        onClick={goToEditRenewal}
                        className="request-details__button--payments"
                        {...iconOptions}
                        icon="autoRenew"
                        data-testid="details-request-change-button"
                      >
                        {common('request.details.requestRenewal')}
                      </BaseButtonOutline>
                    </div>
                  )}
                </>
              )}
              {isAmendment && (
                <div className="base-status__info">
                  <BaseButtonOutline
                    onClick={onCancelAmendmentClick}
                    {...iconOptions}
                    icon="delete"
                    data-testid="details-request-change-button"
                  >
                    {common('request.details.cancelChange')}
                  </BaseButtonOutline>
                </div>
              )}
            </>
          )}

          <RequestDeletionButton
            createdAt={createdAt}
            isHidden={props.isDeletionProcessStarted}
            onDeletionRequestDone={handleDeletionRequestDone}
            formVariant={formVariant}
          />
        </div>
      )
    );
  };

  const childrenWithConfirmationsRef = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // Check if this is the Confirmations component or a wrapper that might contain it
      if (child.type.displayName === 'Confirmations' || child.type.name === 'Confirmations') {
        return React.cloneElement(child, { setConfirmationsRef });
      }

      // If this child might contain Confirmations deeper in its tree, pass the prop down
      if (child.props && child.props.children) {
        return React.cloneElement(child, {
          children: React.Children.map(child.props.children, (grandchild) => {
            if (
              React.isValidElement(grandchild) &&
              (grandchild.type.displayName === 'Confirmations' || grandchild.type.name === 'Confirmations')
            ) {
              return React.cloneElement(grandchild, { setConfirmationsRef });
            }
            return grandchild;
          }),
        });
      }
    }
    return child;
  });

  return (
    <div
      className={classnames('request-wrapper', {
        [`request-wrapper--${formVariant}`]: formVariant,
      })}
    >
      <div className="container">{!isMainForm ? <div className="row">{showDetails(formVariant)}</div> : null}</div>
      <div className="container">
        <div className="row">
          {isMainForm && (
            <div className="col-2">
              {goBack && (
                <BaseButtonLink
                  iconLeft
                  icon="arrow-left"
                  onClick={() => navigate(goBack)}
                  className="request-wrapper__go-back"
                  data-testid="go-back-button"
                >
                  {common('goBack')}
                </BaseButtonLink>
              )}

              <>
                {showDetails(formVariant)}
                {showStatus()}
                {request.status !== 0 && <div className="request-wrapper__info">{content}</div>}
                {isClientAdvisorBadgeVisible && (
                  <ClientAdvisorBadge
                    clientId={request.case_id}
                    href={request.badge_zip_file}
                    image={request.badge_png_file}
                  />
                )}
              </>
            </div>
          )}

          {!isMainForm && (
            <div className="col-3 request-wrapper__sidebar">
              {isRequestDetails && (
                <>
                  {
                    <div className="request-wrapper__category">
                      {Object.values(categories).map((category) => {
                        return (
                          <div key={category}>
                            {category === categories.downloadQr || category === categories.history ? null : (
                              <BaseButtonOutline
                                type="button"
                                left
                                large
                                category
                                className={classnames({
                                  'base-button__category--active': activeCategory === category,
                                })}
                                onClick={() => setActiveCategory(category)}
                              >
                                {category}
                              </BaseButtonOutline>
                            )}
                          </div>
                        );
                      })}
                      <div className="request-wrapper__category--bottom">
                        {Object.values(categories).map((category) =>
                          (category === categories.downloadQr && isClientAdvisorBadgeVisible) ||
                          category === categories.history ? (
                            <div key={category}>
                              <BaseButtonOutline
                                type="button"
                                left
                                large
                                category
                                className={classnames('base-button__category--secondary', {
                                  'base-button__category--active': activeCategory === category,
                                })}
                                onClick={() => setActiveCategory(category)}
                              >
                                {category}
                              </BaseButtonOutline>
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  }
                  {showStatus()}
                </>
              )}
              {isRequestForm && !isIntro && (
                <div
                  className={classnames('request-wrapper__stepper', {
                    'request-wrapper__stepper--amendment': isIntro,
                  })}
                >
                  <Steps
                    orientation="vertical"
                    current={activeStep - 1}
                    items={steps.map((step) => ({ title: step[1] }))}
                  />
                </div>
              )}
            </div>
          )}

          <div className="col-8 request-wrapper__form">{childrenWithConfirmationsRef}</div>
          {isRequestDetails && <div className="col-1" />}
          {isMainForm && <div className="col-2" />}
        </div>
      </div>
      {isMainForm && isAmendment && (
        <div className="request-wrapper__footer">
          <div className="container request-wrapper__footer--summary">
            <RequestSummaryFooter submit={submitAmendment} />
          </div>
        </div>
      )}
      {isMainForm && isFormEditable(request.status) && (
        <div className="request-wrapper__footer">
          {isFinal ? (
            <div className="container request-wrapper__footer--summary">
              {isPaymentSuccess && <PaymentButton ref={paymentRef} isRenewal={false} onClose={onPaymentClose} />}
              <RequestSummaryFooter submit={saveAndSubmit} />
            </div>
          ) : (
            <RequestFooter
              noSteps={noSteps}
              saveAndContinue={saveAndContinue}
              saveAndReturn={saveAndReturn}
              disabled={disabled}
              {...props}
            />
          )}
        </div>
      )}
      {isRequestForm && !isIntro && (
        <div
          className={classnames('request-wrapper__footer', {
            'fields-activity': isFieldsOfActivity,
            'fields-activity--summary': isFieldsOfActivity && !editForm,
          })}
        >
          <div className="container request-wrapper__footer--summary">
            {isAmendment && !isRenewal && !isEmployerChange && (
              <RequestFooter
                saveAndContinue={amendmentSaveAndSubmit}
                previousStep={handleBack}
                currentStep={activeStep}
                disabled={disabled}
                variant={REQUEST_FORM}
                cancelAmendment={onCancelAmendmentClick}
                isSummary={isSummary}
                {...props}
              />
            )}
            {!isAmendment && !isRenewal && !isEmployerChange && (
              <>
                <RequestFooter
                  saveAndContinue={saveAndContinueSteps}
                  previousStep={handleBack}
                  currentStep={activeStep}
                  disabled={disabled}
                  variant={REQUEST_FORM}
                  isSummary={isSummary}
                  isSubmit={isPaymentsConfirmations}
                  isUpdateRequire={isUpdateRequire}
                  {...props}
                />
                {isPaymentSuccess && isFinal && <PaymentButton ref={paymentRef} isRenewal={false} onClose={onPaymentClose} />}
              </>
            )}
            {isEmployerChange && (
              <>
                <RequestFooter
                  saveAndContinue={employerChangeSaveAndSubmit}
                  previousStep={handleBack}
                  currentStep={activeStep}
                  disabled={disabled}
                  variant={REQUEST_FORM}
                  isSummary={isSummary}
                  isEmployerChange={isEmployerChange}
                  cancelAmendment={isPaymentsConfirmations ? onCancelAmendmentClick : onCancelAmendmentClick}
                  isSubmit={isPaymentsConfirmations}
                  {...props}
                />
                {isPaymentSuccessEmployerChange && isFinal && (
                  <PaymentButton ref={paymentRef} isRenewal={false} isEmployerChange={isEmployerChange} onClose={onPaymentClose} />
                )}
              </>
            )}
            {isRenewal && (
              <>
                <RequestFooter
                  saveAndContinue={renewalSaveAndSubmit}
                  previousStep={handleBackRenewal}
                  nextStep={handleNext}
                  editForm={editForm}
                  goToEditForm={goToEditForm}
                  cancelEditForm={cancelEditForm}
                  currentStep={activeStep}
                  disabled={disabled}
                  variant={REQUEST_FORM}
                  cancelAmendment={isPaymentsConfirmations ? onCancelRenewalPayment : onCancelAmendmentClick}
                  isSummary={isSummary}
                  isEmployerChange={isEmployerChange}
                  isRenewal={isRenewal}
                  isSubmit={isPaymentsConfirmations}
                  isCodeOfConduct={isCodeOfConduct}
                  {...props}
                />
                {isPaymentSuccessRenewal && isFinal && (
                  <PaymentButton ref={paymentRef} isRenewal={isRenewal} isEmployerChange={false} onClose={onPaymentClose} />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestStepWrapper;
